import { getSessionItemWithExpiry } from './utility';
import { SESSION_STORAGE_KEY } from '../constant/auth.constant';
import { USER_TYPE } from '../constants';

/**
 * Checks if the currently logged-in user has the specified privilege.
 *
 * @param privilegeName - The privilege name to check (from APP_FEATURES enum)
 * @returns true if user has the privilege, false otherwise
 *
 * @example
 * import { hasPrivilege } from '@/utility/hasPrivilege';
 * import { APP_FEATURES } from '@/constants';
 *
 * const canRevokePayment = hasPrivilege(APP_FEATURES.REVOKE_PAYMENT);
 * if (canRevokePayment) {
 *   // Show revoke icon/button
 * }
 */
export const hasPrivilege = (privilegeName: string): boolean => {
  try {
    const storedPrivileges = getSessionItemWithExpiry(SESSION_STORAGE_KEY.PRIVILEGES);

    if (!storedPrivileges) {
      return false;
    }

    const privileges: string[] = JSON.parse(storedPrivileges);
    return privileges.includes(privilegeName);
  } catch (error) {
    console.error('Error checking privilege:', error);
    return false;
  }
};

/**
 * Checks if the user has ANY of the specified privileges (OR logic).
 *
 * @param privilegeNames - Array of privilege names to check
 * @returns true if user has at least one privilege, false otherwise
 *
 * @example
 * import { hasAnyPrivilege } from '@/utility/hasPrivilege';
 * import { APP_FEATURES } from '@/constants';
 *
 * const canManageFinance = hasAnyPrivilege([
 *   APP_FEATURES.FEE_TEMPLATE_MANAGEMENT,
 *   APP_FEATURES.REVOKE_PAYMENT
 * ]);
 */
export const hasAnyPrivilege = (privilegeNames: string[]): boolean => {
  try {
    const storedPrivileges = getSessionItemWithExpiry(SESSION_STORAGE_KEY.PRIVILEGES);

    if (!storedPrivileges) {
      return false;
    }

    const privileges: string[] = JSON.parse(storedPrivileges);
    return privilegeNames.some((privilege) => privileges.includes(privilege));
  } catch (error) {
    console.error('Error checking privileges:', error);
    return false;
  }
};

/**
 * Checks if the user has ALL of the specified privileges (AND logic).
 *
 * @param privilegeNames - Array of privilege names to check
 * @returns true if user has all privileges, false otherwise
 *
 * @example
 * import { hasAllPrivileges } from '@/utility/hasPrivilege';
 * import { APP_FEATURES } from '@/constants';
 *
 * const canFullyManageFinance = hasAllPrivileges([
 *   APP_FEATURES.FEE_TEMPLATE_MANAGEMENT,
 *   APP_FEATURES.REVOKE_PAYMENT
 * ]);
 */
export const hasAllPrivileges = (privilegeNames: string[]): boolean => {
  try {
    const storedPrivileges = getSessionItemWithExpiry(SESSION_STORAGE_KEY.PRIVILEGES);

    if (!storedPrivileges) {
      return false;
    }

    const privileges: string[] = JSON.parse(storedPrivileges);
    return privilegeNames.every((privilege) => privileges.includes(privilege));
  } catch (error) {
    console.error('Error checking privileges:', error);
    return false;
  }
};

/**
 * Returns true if the provided role corresponds to an Admin or SuperAdmin.
 * Used to centralize role checks across the frontend.
 */
export const isAdminOrSuperAdmin = (role?: string): boolean => {
  if (!role) return false;
  return role === USER_TYPE.ADMIN || role === USER_TYPE.SUPER_ADMIN;
};
